import React, { useState, useEffect } from 'react';
import { Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import { reportPictureService, type PatientReportPicture } from '../../lib/services.patientinfo';
import { patientService } from '../../lib/services';
import toast from 'react-hot-toast';

const ReportPicture: React.FC = () => {
  const [pictures, setPictures] = useState<PatientReportPicture[]>([]);
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    report_date: new Date().toISOString().split('T')[0],
    patient_search: '',
    patient_id: '',
    notes: ''
  });
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    loadPictures();
    loadPatients();
  }, []);

  const loadPictures = async () => {
    try {
      const data = await reportPictureService.getAll();
      setPictures(data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load report pictures');
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async () => {
    try {
      const data = await patientService.getAll();
      setPatients(data);
    } catch (error: any) {
      console.error('Failed to load patients:', error);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile || !formData.patient_id) {
      toast.error('Please select a patient and file');
      return;
    }

    setUploading(true);
    try {
      const pictureUrl = await reportPictureService.uploadPicture(selectedFile, formData.patient_id);
      
      await reportPictureService.create({
        patient_id: formData.patient_id,
        report_date: formData.report_date,
        picture_url: pictureUrl,
        notes: formData.notes || undefined,
        uploaded_by: 'Current User'
      });

      toast.success('Report picture uploaded successfully');
      setSelectedFile(null);
      setFormData({
        report_date: new Date().toISOString().split('T')[0],
        patient_search: '',
        patient_id: '',
        notes: ''
      });
      loadPictures();
    } catch (error: any) {
      toast.error(error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this report picture?')) return;
    try {
      await reportPictureService.delete(id);
      toast.success('Report picture deleted successfully');
      loadPictures();
    } catch (error: any) {
      toast.error(error.message || 'Delete failed');
    }
  };

  const filteredPatients = patients.filter(p => 
    formData.patient_search && (
      p.name.toLowerCase().includes(formData.patient_search.toLowerCase()) ||
      p.unique_id?.toLowerCase().includes(formData.patient_search.toLowerCase()) ||
      p.phone?.includes(formData.patient_search)
    )
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Report Picture Upload</h1>
          <p className="text-gray-600">Upload patient report pictures with date and notes.</p>
        </div>
      </div>

      {/* Upload Form */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
            <input
              type="date"
              value={formData.report_date}
              onChange={(e) => setFormData({ ...formData, report_date: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Find Patient *</label>
            <input
              type="text"
              value={formData.patient_search}
              onChange={(e) => setFormData({ ...formData, patient_search: e.target.value, patient_id: '' })}
              placeholder="Name, Phone, ID & Select"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
            
            {formData.patient_search && filteredPatients.length > 0 && (
              <div className="mt-2 border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
                {filteredPatients.map(patient => (
                  <button
                    key={patient.id}
                    type="button"
                    onClick={() => setFormData({ 
                      ...formData, 
                      patient_id: patient.id,
                      patient_search: `${patient.name} (${patient.unique_id})`
                    })}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-200 last:border-b-0"
                  >
                    <div className="text-sm font-medium text-gray-900">{patient.name}</div>
                    <div className="text-xs text-gray-500">
                      {patient.unique_id} • {patient.phone}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Picture Upload *</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
            {selectedFile && (
              <p className="mt-1 text-sm text-gray-600">Selected: {selectedFile.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
              placeholder="Add any notes about this report..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          <button
            type="submit"
            disabled={uploading || !selectedFile || !formData.patient_id}
            className="flex items-center space-x-2 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4" />
            <span>{uploading ? 'Submitting...' : 'Submit'}</span>
          </button>
        </form>
      </div>

      {/* Pictures Grid */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Uploaded Report Pictures</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pictures.map((picture) => {
            const patient = patients.find(p => p.id === picture.patient_id);
            return (
              <div key={picture.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="aspect-w-16 aspect-h-12 bg-gray-100">
                  {picture.picture_url ? (
                    <img
                      src={picture.picture_url}
                      alt="Report picture"
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-48">
                      <ImageIcon className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{patient?.name || 'Unknown'}</h3>
                      <p className="text-xs text-gray-500">{patient?.unique_id || '-'}</p>
                    </div>
                    <button
                      onClick={() => handleDelete(picture.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 mb-2">
                    Date: {new Date(picture.report_date).toLocaleDateString()}
                  </p>
                  {picture.notes && (
                    <p className="text-xs text-gray-700 line-clamp-2">{picture.notes}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {pictures.length === 0 && (
          <div className="text-center py-12">
            <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No report pictures uploaded yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportPicture;
